<script setup lang="ts">
import {computed, onMounted, ref} from 'vue';
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Select from "primevue/select";
import Panel from "primevue/panel";
import Button from "primevue/button";
import Skeleton from "primevue/skeleton";
import {DateTime} from "luxon";
import {timeoffBackend, TimeoffStatsDTO} from "@/backend/timeoff.backend.ts";
import {peopleBackend} from "@/backend/people.backend.ts";

const todayYear = DateTime.local().year;
const selectedYear = ref(todayYear);
const stats = ref<TimeoffStatsDTO[] | null>(null);
const departments = ref<any[]>([]);
const loading = ref(false);
const dt = ref();

const years = computed(() => {
  const currentYear = DateTime.local().year;
  const yearsList = [];
  for (let i = currentYear; i >= 2023; i--) {
    yearsList.push(i);
  }
  return yearsList;
});

const statsWithDepartment = computed(() => {
  if (!stats.value || !departments.value) return [];
  
  return stats.value.map(stat => {
    const department = departments.value.find(d => d.id === stat.departmentId);
    return {
      ...stat,
      departmentName: department?.name || 'N/A'
    };
  });
});

async function loadStats() {
  loading.value = true;
  try {
    stats.value = await timeoffBackend.getStats(selectedYear.value);
  } finally {
    loading.value = false;
  }
}

async function loadDepartments() {
  const result = await peopleBackend.departments();
  departments.value = result as any[];
}

onMounted(async () => {
  await loadDepartments();
  await loadStats();
});

const onYearChange = async () => {
  await loadStats();
};

const exportCSV = () => {
  dt.value.exportCSV();
};
</script>

<template>
  <div>
    <Panel class="h-fit">
      <template #header>
        <div class="flex justify-between items-center w-full">
          <h3 class="text-lg font-semibold">Time Off Statistics</h3>
          <div class="flex gap-2 items-center">
              <Select 
              :options="years" 
              v-model="selectedYear" 
              @change="onYearChange"
              class="w-32"
              />
              <Button 
                icon="pi pi-file-excel" 
                @click="exportCSV" 
                v-tooltip.top="'Download CSV'"
                outlined
                text
              />
          </div>
        </div>
      </template>
      
      <div v-if="loading">
        <Skeleton width="100%" height="400px"></Skeleton>
      </div>
      
      <DataTable
        v-else-if="statsWithDepartment.length > 0"
        ref="dt"
        :value="statsWithDepartment"
        sortMode="single"
        scrollable
        scroll-height="60vh"
        class="text-sm"
      >
        <Column field="name" header="Employee" sortable style="min-width: 200px"></Column>
        <Column field="departmentName" header="Department" sortable style="min-width: 150px"></Column>
        <Column field="total" header="Total Days" sortable style="min-width: 120px" class="font-semibold"></Column>
        <Column field="vacation" header="Vacation" sortable style="min-width: 100px"></Column>
        <Column field="personal" header="Personal" sortable style="min-width: 100px"></Column>
        <Column field="sick" header="Sick" sortable style="min-width: 100px"></Column>
        <Column field="parental" header="Parental" sortable style="min-width: 100px"></Column>
        <Column field="bereavement" header="Bereavement" sortable style="min-width: 120px"></Column>
      </DataTable>
      
      <div v-else class="text-center p-4 text-surface-500">
        <p>No time off data available for {{ selectedYear }}</p>
      </div>
    </Panel>
  </div>
</template>
